
import { defineComponent, ref, reactive, toRefs, onMounted, onActivated, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useUserInfoStore } from "@/stores/UserInfoStore";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import { Search } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import IResManufMethodDTO from "@/models/dto/IResManufMethodDTO";
import IResKaizenItemDTO from "@/models/dto/IResKaizenItemDTO";
import ProjectSearch from "@/components/ProjectSearch.vue";
import KaizenItemSearch from "@/components/KaizenItemSearch.vue";
import { dateFormat } from "@/plugins/utility";
import IResProjectInfoDTO from "@/models/dto/IResProjectInfoDTO";
import IReqPNInfoSearchCriteriaDTO from "@/models/dto/IReqPNInfoSearchCriteriaDTO";
import IResPNInfo2DTO from "@/models/dto/IResPNInfo2DTO";
import KaizenItemViewModel from "@/models/KaizenItemViewModel";

export default defineComponent({
  components: {
    KaizenItemSearch,
    ProjectSearch,
  },
  setup() {
    const { t } = useI18n();
    const userInfoStore = useUserInfoStore();

    const state = reactive({
      formLabelPosition: "right" as "top" | "left" | "right",
      formItemSize: "default" as "default" | "small",
      formLabelWidth: "200px" as string,
      today: "",

      // 新增
      addForm: new KaizenItemViewModel(),
      isFormLoading: false,

      //料號查詢
      candidatePN: [] as IResPNInfo2DTO[],
      isPNListLoading: false,

      // 工藝類別選項
      allManufMethodList: [] as IResManufMethodDTO[],
      isManufMethodListLoading: false,

      // 複製彈窗
      isShowKaizenItemSearchDialog: false as boolean,
      targetKaizenItem: {} as IResKaizenItemDTO,

      // 查詢彈窗
      isShowProjectSearchDialog: false as boolean,
      targetProject: {} as IResProjectInfoDTO,
    });

    //#region 建立表單ref與Validator
    const targetPNValidator = async (rule: any, value: string, callback: any) => {
      if (!state.addForm.apsPNInfo) {
        return callback(new Error(t("validation_msg.part_no_is_required")));
      }
      return callback();
    };

    const workNumValidator = async (rule: any, value: any, callback: any) => {
      if (!state.addForm.apsRoutingInfo) {
        return callback(new Error(t("validation_msg.work_num_is_required")));
      }
      return callback();
    };

    const projectValidator = async (rule: any, value: any, callback: any) => {
      if (!state.addForm.project) {
        return callback(new Error(t("validation_msg.project_code_is_required")));
      }
      return callback();
    };
    const manufMethodValidator = async (rule: any, value: any, callback: any) => {
      if (!state.addForm.manufMethod) {
        return callback(new Error(t("validation_msg.manuf_method_is_required")));
      }
      return callback();
    };

    const procTimeAfterImpValidator = async (rule: any, value: any, callback: any) => {
      if (!value) return callback(new Error(t("validation_msg.processing_time_is_required")));
    };

    const newKaizenFormRef = ref();
    const newKaizenFormRules = ref({
      projectCode: [{ required: true, validator: projectValidator, trigger: "blur" }],
      apsPNInfo: [{ required: true, validator: targetPNValidator, trigger: "blur" }],
      workNum: [{ required: true, validator: workNumValidator, trigger: "blur" }],
      manufMethod: [{ required: true, validator: manufMethodValidator, trigger: "blur" }],
      impPlan: [{ required: true, message: () => t("form.validation_msg.improvement_plan_is_required"), trigger: "blur" }],
      impMethod: [{ required: true, message: () => t("form.validation_msg.improvement_method_is_required"), trigger: "blur" }],
      impMethodDetail: [{ required: true, message: () => t("form.validation_msg.implement_detail_is_required"), trigger: "blur" }],
      implementDay: [{ required: true, message: () => t("form.validation_msg.implement_day_is_required"), trigger: "blur" }],

      procTimeAfterImp: [{ required: true, validator: procTimeAfterImpValidator, trigger: "blur" }],
    });

    const projectSearchRef = ref(ProjectSearch);
    const kaizenItemSearchRef = ref(KaizenItemSearch);
    //#endregion

    //#region Hook functions
    onMounted(() => {
      console.log(`onMounted : displayName=${userInfoStore.displayName}`);
      setBasicInfo();
    }),
      onActivated(() => {
        console.log(`onActivated : displayName=${userInfoStore.displayName}`);
        setBasicInfo();
      });
    //#endregion

    //#region UI Events
    /** 點擊專案代碼輸入欄位 */
    const onPJCodeInputFieldClicked = async () => {
      console.log("onPJCodeInputFieldClicked");
      if (state.addForm.project) return;
      await showProjectSearchDialog();
    };

    /** 按下查詢專案按鈕 */
    const onSearchProjectBtnClicked = async () => {
      console.log("onSearchProjectBtnClicked");
      await showProjectSearchDialog();
    };

    /** 於Dialog內選擇專案 */
    const onProjectSelected = (project: IResProjectInfoDTO) => {
      state.targetProject = project;
    };

    /** 完成專案選擇 */
    const onSelectProjectConfirmClicked = () => {
      if (!state.targetProject) return;

      if (state.addForm.project && state.targetProject.id === state.addForm.project.id) {
        state.isShowProjectSearchDialog = false;
        return;
      }

      console.log(`onProjectSelectChanged.  ${state.targetProject.id}, ${state.targetProject.pjCode}`);

      state.addForm.project = state.targetProject;
      state.addForm.projectCode = state.targetProject.pjCode;

      //重新執行projectCode區域的validator
      newKaizenFormRef.value?.validateField("projectCode");

      //重置料號選擇內容
      state.candidatePN.length = 0;
      state.addForm.apsPNInfo = undefined;
      state.addForm.apsRoutingInfo = undefined;

      state.isShowProjectSearchDialog = false;
    };

    /** 開啟複製彈窗 */
    const onCopyBtnClicked = async () => {
      await showKaizenItemSearchDialog();
    };

    /** 於Dialog內選擇改善事項 */
    const onKaizenItemSelected = (kaizenItem: IResKaizenItemDTO) => {
      state.targetKaizenItem = kaizenItem;
    };

    /** 完成改善事項複製選擇 */
    const onSelectKaizenItemConfirmClicked = () => {
      if (!state.targetKaizenItem) return;

      //除專案代碼、料號、改善前工時、改善後工時、作業編號其他皆要複製

      state.addForm.manufMethod = state.allManufMethodList.find((x) => x.id === state.targetKaizenItem.manufMethodId);
      state.addForm.impPlan = state.targetKaizenItem.impPlan;
      state.addForm.impMethod = state.targetKaizenItem.impMethod;
      state.addForm.impMethodDetail = state.targetKaizenItem.impMethodDetail;
      state.addForm.hyperLink = state.targetKaizenItem.hyperlink;

      state.isShowKaizenItemSearchDialog = false;
    };

    /** 按下清除按鈕 */
    const onClearFormClicked = async () => {
      try {
        await ElMessageBox.confirm(t("clear_confirm_msg"), t("Warning"), {
          confirmButtonText: t("Confirm"),
          cancelButtonText: t("Cancel"),
          type: "warning",
        });

        await resetFormDataAsync();
      } catch (error) {
        console.log(error);
      }
    };

    /** 顯示工藝類別下拉選單時 */
    const onManufMethodListShown = async () => {
      try {
        state.isManufMethodListLoading = true;
        await updateManufMethodList();
      } catch (error) {
        console.log(error);
      } finally {
        state.isManufMethodListLoading = false;
      }
    };

    /** 依使用者輸入的字串提供料號選擇建議 */
    const onPNSuggestionListShown = async (queryString: string) => {
      //console.log(`onPNSuggestionListShown : ${queryString}`);
      if (!state.addForm.project) {
        console.warn("project is undefined");
        return;
      }

      state.isPNListLoading = true;
      try {
        state.candidatePN.length = 0;
        const searchCriteria: IReqPNInfoSearchCriteriaDTO = {
          pnKeyword: queryString,
          siteName: state.addForm.project.siteName,
          takeNum: 10,
        };
        const searchPNResponse = await API.searchPartInfoV2(searchCriteria);
        if (searchPNResponse.data.result !== APIResultCode.success) {
          throw new Error(searchPNResponse.data.msg);
        }
        state.candidatePN = searchPNResponse.data.content;
      } catch (error) {
        console.log(error);
      } finally {
        state.isPNListLoading = false;
      }
    };

    /** 選擇改善料號 */
    const onPNSelectChanged = () => {
      //重新執行projectCode區域的validator (此時應該有值, 告警訊息應該消除)
      newKaizenFormRef.value?.validateField("apsPNInfo");
      state.addForm.apsRoutingInfo = undefined;
    };

    /** 選擇作業編號 */
    const onWorkNumSelectChanged = () => {
      //重新執行jobNumber區域的validator (此時應該有值, 告警訊息應該消除)
      newKaizenFormRef.value?.validateField("workNum");
    };

    const onManufMethodSelectChanged = () => {
      //重新執行manufMethod區域的validator (此時應該有值, 告警訊息應該消除)
      newKaizenFormRef.value?.validateField("manufMethod");
    };

    /** 按下送出 */
    const onSubmitFormClicked = async () => {
      
      try {
        await newKaizenFormRef.value?.validate();  
      } catch (error) {
        console.log(error);
        await nextTick();
        const isError = document.getElementsByClassName('is-error')[0];
        isError.scrollIntoView({
          block:'center',
          behavior:'smooth'
        });
        return;
      }

      try {

        await ElMessageBox.confirm(t("Create_Confirm_Msg"), t("Warning"), {
          confirmButtonText: t("Confirm"),
          cancelButtonText: t("Cancel"),
          type: "warning",
        });
      } catch (error) {
        console.log(error);
        return;
      }

      try {
        state.isFormLoading = true;

        const requestDTO = state.addForm.ToIReqKaizenItemDTO();
        const createKaizenItemResponse = await API.createKaizenItem(requestDTO);

        if(createKaizenItemResponse.data.result == APIResultCode.duplicate_kaizen_item) {
          //偵測到重複的項目(同期同一個料號同一個作業編號只允許出現一筆)
          ElNotification({
            title: t("error"),
            message: t("duplicate_kaizen_item"),
            type: "error",
          });
          return;
        }

        if (createKaizenItemResponse.data.result !== APIResultCode.success) {
          throw new Error(createKaizenItemResponse.data.msg);
        }

        ElMessage({
          showClose: true,
          message: "新增成功",
          type: "success",
        });

        await resetFormDataAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isFormLoading = false;
      }
    };

    /** 按下Sharepoint連結 */
    const onSharepointClicked = async () => {
      console.log("click");
      const url = "http://tpspap/sites/TPS/Documents/Forms/AllItems.aspx";
      window.open(url, "_blank");
    }
    //#endregion

    //#region Private Functions
    /** 重置所有表單資料 */
    async function resetFormDataAsync() {
      state.addForm = new KaizenItemViewModel();
      state.candidatePN.length = 0;

      await nextTick();

      newKaizenFormRef.value?.clearValidate();

      setBasicInfo();
    }

    /** 設置基本建立資訊(建立者、建立日期) */
    function setBasicInfo() {
      //state.addForm.creator = userInfoStore.displayName; //建立者資訊直接從userInfoStore提取
      if (window.innerWidth <= 576) {
        state.formLabelPosition = "top";
        state.formItemSize = "default";
        state.formLabelWidth = "auto";
      }
      state.today = dateFormat(new Date());
      state.addForm.implementDay = new Date();
    }

    /** 顯示專案查詢dialog(重置查詢結果) */
    async function showProjectSearchDialog() {
      state.isShowProjectSearchDialog = true;
      console.log("showProjectSearchDialog");

      await nextTick();
      await projectSearchRef.value?.resetSearch();
    }

    /** 顯示改善事項查詢dialog(重置查詢結果) */
    async function showKaizenItemSearchDialog() {
      state.isShowKaizenItemSearchDialog = true;
      await nextTick();
      await kaizenItemSearchRef.value?.resetSearch();
    }

    /** 呼叫API, 取得所有工藝類別 */
    async function updateManufMethodList() {
      const getAllManufMethodResponse = await API.getManufMethods();

      if (getAllManufMethodResponse.data.result !== APIResultCode.success) {
        throw new Error(getAllManufMethodResponse.data.msg);
      }

      state.allManufMethodList = getAllManufMethodResponse.data.content;
    }
    //#endregion

    return {
      t,
      ...toRefs(state),
      newKaizenFormRef,
      newKaizenFormRules,
      projectSearchRef,
      kaizenItemSearchRef,

      // icons
      Search,

      // function
      onPJCodeInputFieldClicked,
      onSearchProjectBtnClicked,
      onClearFormClicked,
      onManufMethodListShown,
      onCopyBtnClicked,
      onProjectSelected,
      onKaizenItemSelected,
      onPNSuggestionListShown,
      onPNSelectChanged,
      onWorkNumSelectChanged,
      onManufMethodSelectChanged,
      onSelectProjectConfirmClicked,
      onSubmitFormClicked,
      onSelectKaizenItemConfirmClicked,
      onSharepointClicked,

      userInfoStore,
    };
  },
});
